// A MapLibre instance inside a modal is heavy and slow for what is a shape-vs-shape judgement, so
// we project the rings into a shared SVG viewBox instead: equal-aspect (lon scaled by cos(lat)) so
// shapes aren't sheared, y flipped so north is up.

type Ring = [number, number][];

// every ring of any Polygon/MultiPolygon (outer + holes; holes matter for the outline to read correctly)
export function ringsOf(geom: unknown): Ring[] {
  const g = geom as { type?: string; coordinates?: unknown } | null | undefined;
  if (!g || typeof g !== "object") return [];
  if (g.type === "Polygon") return (g.coordinates as Ring[]) ?? [];
  if (g.type === "MultiPolygon")
    return ((g.coordinates as Ring[][]) ?? []).flat();
  return [];
}

export interface PreviewBox {
  width: number;
  height: number;
  /* * project a lon/lat ring into an SVG path (`M…L…Z`); '' when the ring is unusable */
  path: (ring: Ring) => string;
}

/* * Build a shared projection over ALL the shapes being drawn, so they stay in register with each other. */
export function previewBox(
  geoms: unknown[],
  size = 220,
  pad = 6,
): PreviewBox | null {
  const rings = geoms.flatMap(ringsOf).filter((r) => r.length >= 3);
  if (!rings.length) return null;
  let w = Infinity,
    s = Infinity,
    e = -Infinity,
    n = -Infinity;
  for (const r of rings)
    for (const [x, y] of r) {
      if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
      if (x < w) w = x;
      if (x > e) e = x;
      if (y < s) s = y;
      if (y > n) n = y;
    }
  if (
    !Number.isFinite(w) ||
    !Number.isFinite(s) ||
    !Number.isFinite(e) ||
    !Number.isFinite(n)
  )
    return null;
  // equal-aspect: a degree of longitude is shorter than a degree of latitude away from the equator
  const kx = Math.cos(((s + n) / 2) * (Math.PI / 180)) || 1;
  const spanX = Math.max((e - w) * kx, 1e-9);
  const spanY = Math.max(n - s, 1e-9);
  const scale = (size - pad * 2) / Math.max(spanX, spanY);
  // floor the box: a very tall/narrow (or degenerate) pairing would otherwise render a ~12px-wide sliver
  const width = Math.max(spanX * scale + pad * 2, 40);
  const height = Math.max(spanY * scale + pad * 2, 40);
  const path = (ring: Ring): string => {
    if (!ring || ring.length < 3) return "";
    let d = "";
    let kept = 0; // NB: not `n` — that's the north bound in the enclosing scope
    for (const [lng, lat] of ring) {
      // SKIP a bad vertex rather than discarding the whole ring — the bbox pass skips them too, so
      // rejecting here would erase a shape that had already influenced the projection.
      if (!Number.isFinite(lng) || !Number.isFinite(lat)) continue;
      const px = (lng - w) * kx * scale + pad;
      const py = height - pad - (lat - s) * scale; // flip: north up
      d += `${kept === 0 ? "M" : "L"}${px.toFixed(1)},${py.toFixed(1)}`;
      kept++;
    }
    return kept >= 3 ? `${d}Z` : "";
  };
  return { width, height, path };
}

/* * "2.4 ac (18% of the new field)" — the human magnitude next to a hit. */
export function overlapSummary(hit: {
  overlap_acres?: number | null;
  overlap_pct_of_candidate?: number | null;
}): string {
  const ac = hit.overlap_acres;
  const pct = hit.overlap_pct_of_candidate;
  const acTxt =
    typeof ac === "number" && Number.isFinite(ac)
      ? `${ac >= 10 ? Math.round(ac) : Math.round(ac * 10) / 10} ac`
      : "";
  const pctTxt =
    typeof pct === "number" && Number.isFinite(pct)
      ? `${Math.round(pct)}% of the new boundary`
      : "";
  if (acTxt && pctTxt) return `${acTxt} · ${pctTxt}`;
  return acTxt || pctTxt || "overlapping";
}
